import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Papa from 'papaparse';
import { Upload, FileText, Check, AlertCircle, ArrowRight, X, Loader2 } from 'lucide-react';

const FIELD_OPTIONS = [
  { value: '', label: 'Unmapped' },
  { value: 'first_name', label: 'First Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: 'full_name', label: 'Full Name (auto-split)' },
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'last_visit_date', label: 'Last Visit Date' },
  { value: 'appointment_status', label: 'Appointment Status' },
  { value: 'treatment_type', label: 'Treatment Type' },
  { value: 'treatment_status', label: 'Treatment Status' },
  { value: 'last_contact_date', label: 'Last Contact Date' },
  { value: 'notes', label: 'Notes' },
  { value: 'estimated_case_value', label: 'Estimated Value' },
];

const AUTO_MAP = {
  'first name': 'first_name', 'firstname': 'first_name', 'patient first': 'first_name', 'given name': 'first_name',
  'last name': 'last_name', 'lastname': 'last_name', 'surname': 'last_name', 'family name': 'last_name',
  'phone': 'phone', 'phone number': 'phone', 'mobile': 'phone', 'mobile number': 'phone', 'tel': 'phone', 'contact number': 'phone',
  'email': 'email', 'email address': 'email', 'patient email': 'email',
  'last visit': 'last_visit_date', 'last visit date': 'last_visit_date', 'visit date': 'last_visit_date',
  'appointment status': 'appointment_status', 'appt status': 'appointment_status', 'booking status': 'appointment_status',
  'treatment': 'treatment_type', 'treatment type': 'treatment_type', 'service': 'treatment_type', 'procedure': 'treatment_type',
  'treatment status': 'treatment_status', 'plan status': 'treatment_status', 'tx status': 'treatment_status',
  'last contact': 'last_contact_date', 'last contacted': 'last_contact_date', 'contact date': 'last_contact_date',
  'notes': 'notes', 'comments': 'notes', 'staff notes': 'notes', 'patient notes': 'notes',
  'value': 'estimated_case_value', 'case value': 'estimated_case_value', 'estimated value': 'estimated_case_value',
  'treatment value': 'estimated_case_value', 'quote': 'estimated_case_value', 'amount': 'estimated_case_value',
};

export default function ImportData() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [phase, setPhase] = useState('upload'); // upload | mapping | importing | done | error
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState([]);
  const [progress, setProgress] = useState('');

  const handleFile = useCallback((f) => {
    if (!f || !f.name.endsWith('.csv')) {
      setErrors([{ row: 0, error: 'Only .csv files are supported' }]);
      return;
    }
    setFile(f);
    Papa.parse(f, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const hdrs = results.meta.fields || [];
        setHeaders(hdrs);
        setPreviewRows(results.data.slice(0, 5));
        setAllRows(results.data);

        // Auto-map
        const autoMapping = {};
        hdrs.forEach(h => {
          const lower = h.trim().toLowerCase();
          if (AUTO_MAP[lower]) {
            autoMapping[h] = AUTO_MAP[lower];
          } else if (lower === 'name' || lower === 'patient name' || lower === 'full name') {
            autoMapping[h] = 'full_name';
          } else if (lower === 'status') {
            autoMapping[h] = 'appointment_status';
          }
        });
        setMapping(autoMapping);
        setPhase('mapping');
      },
      error: (err) => {
        setErrors([{ row: 0, error: `Parse error: ${err.message}` }]);
      },
    });
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    handleFile(f);
  }, [handleFile]);

  const handleImport = async () => {
    const mappedFields = Object.values(mapping).filter(Boolean);
    const hasName = mappedFields.includes('first_name') || mappedFields.includes('full_name');
    const hasPhone = mappedFields.includes('phone');

    if (!hasName || !hasPhone) {
      setErrors([{ row: 0, error: 'First Name (or Full Name) and Phone are required fields.' }]);
      return;
    }

    setPhase('importing');
    setProgress('Uploading...');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mapping', JSON.stringify(mapping));

      setProgress('Parsing and validating...');
      const { data } = await api().post('/api/import/csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setProgress('AI analysis complete!');
      setResult(data);
      setErrors(data.errors || []);
      setPhase('done');
    } catch (e) {
      setErrors([{ row: 0, error: e.response?.data?.detail || 'Import failed' }]);
      setPhase('error');
    }
  };

  return (
    <div data-testid="import-data-page" className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Import Your Patient Data</h1>
          <p className="text-sm text-slate-500 mt-1">Upload your last 6 months of records. AI will scan for revenue leaks.</p>
        </div>

        {/* Upload Phase */}
        {phase === 'upload' && (
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
            data-testid="csv-drop-zone"
            className="bg-white rounded-xl border-2 border-dashed border-slate-300 p-16 text-center cursor-pointer hover:border-slate-400 hover:bg-slate-50 transition-all"
          >
            <Upload size={40} className="mx-auto text-slate-300 mb-4" />
            <p className="text-sm font-medium text-slate-700">Drag and drop your CSV file here</p>
            <p className="text-xs text-slate-400 mt-2">or click to browse — .csv files only</p>
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => handleFile(e.target.files[0])}
            />
          </div>
        )}

        {/* Mapping Phase */}
        {phase === 'mapping' && (
          <div className="space-y-6">
            {/* File info */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
              <FileText size={20} className="text-slate-400" />
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900">{file?.name}</p>
                <p className="text-xs text-slate-500">{allRows.length} patient records detected</p>
              </div>
              <button onClick={() => { setPhase('upload'); setFile(null); setHeaders([]); }}
                className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
            </div>

            {/* Preview */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-200">
                <h3 className="text-sm font-semibold text-slate-900">Preview (first 5 rows)</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50">
                      {headers.map(h => <th key={h} className="text-left px-3 py-2 font-medium text-slate-600 whitespace-nowrap">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        {headers.map(h => <td key={h} className="px-3 py-2 text-slate-600 whitespace-nowrap max-w-[200px] truncate">{row[h]}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Column Mapping */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-sm font-semibold text-slate-900 mb-4">Map Columns</h3>
              <div className="space-y-3">
                {headers.map(h => (
                  <div key={h} className="flex items-center gap-4">
                    <span className="text-sm text-slate-700 w-48 truncate font-medium">{h}</span>
                    <ArrowRight size={14} className="text-slate-300 shrink-0" />
                    <select
                      data-testid={`map-${h.replace(/\s+/g, '-').toLowerCase()}`}
                      value={mapping[h] || ''}
                      onChange={e => setMapping(prev => ({ ...prev, [h]: e.target.value }))}
                      className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 bg-white"
                    >
                      {FIELD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    {mapping[h] && <Check size={16} className="text-emerald-500 shrink-0" />}
                  </div>
                ))}
              </div>
            </div>

            {/* Errors */}
            {errors.length > 0 && (
              <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                {errors.map((e, i) => (
                  <p key={i} className="text-sm text-red-700 flex items-center gap-2">
                    <AlertCircle size={14} /> {e.error}
                  </p>
                ))}
              </div>
            )}

            <button
              data-testid="import-submit"
              onClick={handleImport}
              className="w-full bg-slate-900 text-white py-3 rounded-lg text-sm font-medium hover:bg-slate-800 transition-all flex items-center justify-center gap-2"
            >
              Import & Scan for Revenue Leaks <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* Importing Phase */}
        {phase === 'importing' && (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Loader2 size={40} className="mx-auto text-slate-900 animate-spin mb-4" />
            <p className="text-lg font-semibold text-slate-900">{progress}</p>
            <p className="text-sm text-slate-500 mt-2">Analyzing with AI... This may take a moment.</p>
          </div>
        )}

        {/* Done Phase */}
        {phase === 'done' && result && (
          <div className="space-y-6">
            <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-8 text-center">
              <Check size={40} className="mx-auto text-emerald-500 mb-4" />
              <h2 className="text-xl font-bold text-slate-900">Import Complete!</h2>
              <p className="text-sm text-slate-600 mt-2">{result.imported} records imported. {result.leaks_detected} revenue leaks detected.</p>
              {result.potential_revenue > 0 && (
                <p className="text-2xl font-bold text-emerald-600 mt-3">${result.potential_revenue.toLocaleString()} in potential revenue leaks</p>
              )}
            </div>

            {errors.length > 0 && (
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                <h3 className="text-sm font-semibold text-amber-800 mb-2">{errors.length} rows skipped</h3>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {errors.map((e, i) => (
                    <p key={i} className="text-xs text-amber-700">Row {e.row}: {e.error}</p>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                data-testid="view-leaks-btn"
                onClick={() => navigate('/revenue-leaks')}
                className="flex-1 bg-slate-900 text-white py-3 rounded-lg text-sm font-medium hover:bg-slate-800 transition-all"
              >
                View Revenue Leaks
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="flex-1 border border-slate-200 text-slate-700 py-3 rounded-lg text-sm font-medium hover:bg-slate-50 transition-all"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        )}

        {/* Error Phase */}
        {phase === 'error' && (
          <div className="bg-red-50 rounded-xl border border-red-200 p-8 text-center">
            <AlertCircle size={40} className="mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-slate-900">Import Failed</h2>
            {errors.map((e, i) => <p key={i} className="text-sm text-red-700 mt-2">{e.error}</p>)}
            <button onClick={() => setPhase('upload')} className="mt-4 bg-slate-900 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-slate-800">
              Try Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
